import { Navigate, Outlet } from 'react-router';
import { Column, Row } from '~/commonComponents';
import { Footer } from '~/commonComponents/Footer';
import About from '~/components/About';
import { AccountInformation } from '~/components/AccountInformation/AccountInformation';
import Alerts from '~/components/Alerts';
import { CancellationFeedbackManager } from '~/components/CancellationFeedback';
import { ConfirmCandidate } from '~/components/ConfirmCandidate/ConfirmCandidate';
import { BulkConfirmCandidateJobs } from '~/components/ConfirmJobs/BulkConfirmCandidateJobs';
import Dashboard from '~/components/Dashboard/Dashboard';
import InvoiceList from '~/components/Dashboard/ReceiptList/InvoiceList';
import ReceiptList from '~/components/Dashboard/ReceiptList/ReceiptList';
import TransactionsInfo from '~/components/Dashboard/ReceiptList/TransactionsInfo';
import EndOfDay from '~/components/EndOfDay';
import ExpiredCounterOffers from '~/components/ExpiredCounterOffers';
import { HelpScreen } from '~/components/Help';
import { HowItWorksScreen } from '~/components/HowItWorksScreen';
import Inbox from '~/components/Inbox';
import JobDetails from '~/components/JobDetails/JobDetails';
import { JobList } from '~/components/JobList/ShiftList';
import { TodayJobs } from '~/components/JobList/TodayJobs';
import JobSummaryRating from '~/components/JobSummaryRating';
import LocumTenensDentist from '~/components/LocumTenensDentist';
import { NPSReview } from '~/components/NPSReview/NPSReview';
import NotificationSettings from '~/components/NotificationSettings/NotificationSettings';
import Payment from '~/components/Payments/Payment';
import PermanentDashboard from '~/components/PermanentJobs/PermanentDashboard';
import PermanentsJobs from '~/components/PermanentJobs/PermanentsJobs';
import {
  CreatePermanentJobPosting,
  CreatePermanentJobPostingSteps,
  CreatePermanentJobsPage,
  IMPORT_CONFIRM_JOB_DETAILS_PATH,
  ManageJobs,
  ValuePropositionPage,
} from '~/components/PermanentJobsV2';
import {
  CreateOnsiteInterviewPage,
  CreateOnsiteInterviewSteps,
  CreateOnsiteInterviewSubPages,
} from '~/components/PermanentJobsV2/CreateOnsiteInterview';
import { EditOnsiteInterviewPage } from '~/components/PermanentJobsV2/EditOnsiteInterview';
import { JobOverview } from '~/components/PermanentJobsV2/JobOverview';
import { ProfessionalProfile } from '~/components/ProfessionalProfile/ProfessionalProfile';
import { ProfessionalsHub } from '~/components/ProfessionalsHub/ProfessionalsHub';
import { InviteProfessional } from '~/components/ProfessionalsHub/components/InviteProfessional/InviteProfessional';
import { OfficeJobInvites } from '~/components/ProfessionalsHub/components/OfficeJobInvites/OfficeJobInvites';
import { ReferralDisclaimerScreen } from '~/components/Referral/ReferralDisclaimerScreen';
import { ReferralScreenV2 } from '~/components/Referral/ReferralScreenV2';
import PPScreen from '~/components/Request/pp/PPScreen';
import { Schedule } from '~/components/Schedule/Schedule';
import { SendFeedback } from '~/components/SendFeedback';
import { ShiftPendingReviewsScreen } from '~/components/ShiftPendingReviews';
import { AddInvitesScreen } from '~/components/ShiftRequestRedesign/AddInvites';
import { EditOpenShiftScreen } from '~/components/ShiftRequestRedesign/EditOpenShift';
import { EditShiftScreen } from '~/components/ShiftRequestRedesign/EditShift';
import { ReviewConfirmScreen } from '~/components/ShiftRequestRedesign/ReviewConfirm';
import { SelectDatesScreen } from '~/components/ShiftRequestRedesign/SelectDates';
import { ShiftDetailsScreen } from '~/components/ShiftRequestRedesign/ShiftDetails';
import { ShiftRequestLayout } from '~/components/ShiftRequestRedesign/ShiftRequestLayout';
import SideBar from '~/components/SideBar';
import TopNavBar from '~/components/TopNavBar';
import VirtualJobRedirection from '~/components/VirtualJobRedirection';
import { WorkerClassification } from '~/components/WorkerClassification';
import { WorkerClassificationManagement } from '~/components/WorkerClassificationManagement';
import AdjustmentScreen from '~/components/adjustments/AdjustmentScreen';
import OpenJobScreen from '~/components/counterJobs/OpenJobScreen';
import Chat from '~/components/messages/Chat';
import Profile from '~/components/profile/Profile';
import { getShouldEnforcePermanentJobV2 } from '~/services/posthog/FeatureFlagUtils';
import { EnablePermanentJobM2M } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { NotAllowedGuard } from '~/routes/AuthenticatedRoutes/NotAllowedGuard';
import { useIsWorkerClassificationEnabled } from '~/selectors';
import { routes } from '..';
import { useAppRoutes, useIsReferralProgramVisible } from '../hooks';
import { TermsAndConditions } from '~/components/TermsAndConditions/TermsAndConditions';
import { CandidateOverview, CandidateOverviewRedirect } from '~/components/CandidateOverview';

export const AuthenticatedRoutes = () => {
  const currentUser = useUserSelector();
  const isWorkerClassificationEnabled = useIsWorkerClassificationEnabled();
  const shouldShowReferralProgram = useIsReferralProgramVisible();

  const hasMissingWorkerClassification =
    isWorkerClassificationEnabled && !currentUser?.workerClassification?.classificationType;
  const isMultiDayShiftRequestAllowed =
    currentUser?.allowedRequest?.tempOrMdbShifts && !hasMissingWorkerClassification;

  const isEnablePermanentJobM2M = useFeatureFlag(EnablePermanentJobM2M);

  return useAppRoutes([
    {
      path: '/',
      element: (
        <Column flex={1} justifyContent="space-between" minHeight={'100%'}>
          <TopNavBar />
          <Row flex={1}>
            <SideBar />
            <Outlet />
          </Row>
          <Footer />
        </Column>
      ),
      children: [
        {
          title: 'Dashboard',
          path: routes.dashboardHome,
          element: <Dashboard />,
        },
        {
          title: 'Account Information',
          path: routes.accountInformation,
          element: <AccountInformation />,
        },
        {
          title: 'Invoices',
          path: routes.invoices,
          element: <InvoiceList />,
        },
        {
          title: 'Edit Open Job',
          path: routes.editOpenJob,
          element: <EditOpenShiftScreen />,
        },
        {
          title: 'Permanent Job Details',
          path: routes.permanentJobDetails,
          element: <PermanentDashboard />,
        },
        {
          title: 'Permanent Jobs',
          path: routes.permanentJobs,
          element: <PermanentsJobs />,
        },
        {
          title: 'Inbox',
          path: routes.inbox,
          element: <Inbox />,
        },
        {
          title: 'Chat',
          path: routes.chat,
          element: <Chat />,
        },
        {
          title: 'Expired Counter Offers',
          path: routes.expiredCounterOffers,
          element: <ExpiredCounterOffers />,
        },
        ...(isMultiDayShiftRequestAllowed
          ? [
              {
                title: 'Shift Request',
                element: <ShiftRequestLayout />,
                children: [
                  {
                    title: 'Select Dates',
                    path: routes.selectDates,
                    element: <SelectDatesScreen />,
                  },
                  {
                    title: 'Multi Day Shift Request',
                    path: routes.multiDayShiftRequest,
                    element: <ShiftDetailsScreen />,
                  },
                  {
                    title: 'Multi Day Shift Add Invites',
                    path: routes.multiDayShiftAddInvites,
                    element: <AddInvitesScreen />,
                  },
                  {
                    title: 'Multi Day Shift Confirm',
                    path: routes.multiDayShiftConfirm,
                    element: <ReviewConfirmScreen />,
                  },
                  {
                    title: 'Edit Shift',
                    path: routes.multiDayShiftEditRedesign,
                    element: <EditShiftScreen />,
                  },
                ],
              },
            ]
          : []),
        {
          title: 'Permanent Hire Request',
          path: routes.permanentHireRequest,
          element: <PPScreen />,
        },
        {
          title: 'Transaction History Details',
          path: routes.transactionHistoryDetails,
          element: <TransactionsInfo />,
        },
        {
          title: 'Payment History',
          path: routes.paymentHistory,
          element: <ReceiptList />,
        },
        {
          title: "Today's Jobs",
          path: routes.todaysJobs,
          element: <TodayJobs />,
        },
        {
          title: 'Pending Review',
          path: routes.pendingReview,
          element: <ShiftPendingReviewsScreen />,
        },
        {
          title: 'Shifts Overview',
          path: routes.shiftsOverview,
          element: <JobList />,
        },
        {
          path: routes.virtualJobRedirection,
          element: <VirtualJobRedirection />,
        },
        {
          title: 'Candidate Profile',
          path: routes.candidateProfile,
          element: <ProfessionalProfile />,
        },
        {
          title: 'Professional Profile',
          path: routes.professionalProfile,
          element: <ProfessionalProfile />,
        },
        {
          title: 'Confirm Candidate',
          path: routes.confirmCandidate,
          element: <ConfirmCandidate />,
        },
        {
          title: 'Job Summary',
          path: routes.jobSummary,
          element: <EndOfDay />,
        },
        {
          title: 'Job Summary Rating',
          path: routes.jobSummaryRating,
          element: <JobSummaryRating />,
        },
        {
          title: 'Job Details',
          path: routes.jobDetails,
          element: <JobDetails />,
        },
        {
          title: 'Open Job Details',
          path: routes.openJobDetails,
          element: <OpenJobScreen />,
        },
        {
          title: 'NPS Review',
          path: routes.npsReview,
          element: <NPSReview />,
        },
        {
          title: 'Alerts',
          path: routes.alerts,
          // @ts-expect-error -- not typed
          element: <Alerts />,
        },
        {
          title: 'Schedule',
          path: routes.schedule,
          element: (
            <NotAllowedGuard>
              <Schedule />
            </NotAllowedGuard>
          ),
        },
        {
          title: 'Payment Information',
          path: routes.paymentInformation,
          element: <Payment />,
        },
        {
          title: 'Help',
          path: routes.help,
          element: <HelpScreen />,
        },
        {
          title: 'About',
          path: routes.about,
          element: <About />,
        },
        {
          title: 'Notification Settings',
          path: routes.notificationSettings,
          element: <NotificationSettings />,
        },
        {
          title: 'Cancellation Feedback',
          path: routes.cancellationFeedback,
          element: <CancellationFeedbackManager />,
        },
        {
          title: 'Send Feedback',
          path: routes.sendFeedback,
          element: <SendFeedback />,
        },
        {
          title: 'Job Adjustment',
          path: routes.jobAdjustment,
          element: <AdjustmentScreen />,
        },
        {
          title: 'How It Works',
          path: routes.howItWorks,
          element: <HowItWorksScreen />,
        },
        {
          title: 'Locum Tenens Dentist',
          path: routes.locumTenensDentist,
          element: <LocumTenensDentist />,
        },
        {
          title: 'Worker Type Setting',
          path: routes.workerClassificationType,
          element: <WorkerClassification isRegistration={false} />,
        },
        {
          title: 'Worker Classification Management',
          path: routes.workerClassificationManagement,
          element: <WorkerClassificationManagement />,
        },
        {
          title: 'Professionals Hub',
          path: routes.professionalsHub,
          element: (
            <NotAllowedGuard>
              <ProfessionalsHub />
            </NotAllowedGuard>
          ),
        },
        {
          title: 'GoTu’s Terms and Conditions',
          path: routes.termsAndConditions,
          element: (
            <NotAllowedGuard>
              <TermsAndConditions />
            </NotAllowedGuard>
          ),
        },
        {
          title: 'Invite Professional',
          path: routes.inviteProfessional,
          element: <InviteProfessional />,
        },
        {
          title: 'Office Job Invites',
          path: routes.officeJobInvites,
          element: <OfficeJobInvites />,
        },
        {
          title: 'Account',
          path: routes.account,
          element: <Profile />,
        },
        {
          path: '*',
          element: <Navigate to={routes.login} />,
        },
        {
          title: 'Bulk Confirm Candidate Jobs',
          path: routes.bulkConfirmCandidateJobs,
          element: <BulkConfirmCandidateJobs />,
        },
        {
          title: "Candidate's Job Detail",
          path: routes.candidateJobDetail,
          element: <ConfirmCandidate isSingleView />,
        },
        {
          title: 'Referral Program',
          path: routes.referral,
          element: (
            <NotAllowedGuard customCondition={!shouldShowReferralProgram}>
              <ReferralScreenV2 />
            </NotAllowedGuard>
          ),
        },
        {
          title: 'Referral Program Disclaimer',
          path: routes.referralDisclaimer,
          element: (
            <NotAllowedGuard>
              <ReferralDisclaimerScreen />
            </NotAllowedGuard>
          ),
        },

        ...(getShouldEnforcePermanentJobV2()
          ? [
              {
                path: '/permanent-jobs/value-proposition',
                element: <ValuePropositionPage />,
              },
              {
                path: '/permanent-jobs/postings',
                element: <ManageJobs />,
              },
              {
                path: '/permanent-jobs/postings/create',
                element: <CreatePermanentJobsPage />,
                children: [
                  {
                    path: '',
                    element: <CreatePermanentJobPosting.BasicInformationPage />,
                  },
                ],
              },
              {
                // Copy & Paste (import job description) flow — separate from the manual guided form.
                path: IMPORT_CONFIRM_JOB_DETAILS_PATH,
                element: <CreatePermanentJobPosting.ConfirmJobDetailsPage />,
              },
              {
                path: '/permanent-jobs/postings/:id/create',
                element: <CreatePermanentJobsPage />,
                children: [
                  {
                    path: '',
                    element: (
                      <Navigate to={CreatePermanentJobPostingSteps.BasicInformation} replace />
                    ),
                  },
                  {
                    path: CreatePermanentJobPostingSteps.BasicInformation,
                    element: <CreatePermanentJobPosting.BasicInformationPage />,
                  },
                  {
                    path: CreatePermanentJobPostingSteps.JobDescription,
                    element: <CreatePermanentJobPosting.JobDescriptionPage />,
                  },
                  {
                    path: CreatePermanentJobPostingSteps.ReviewAndPayment,
                    element: <CreatePermanentJobPosting.ReviewAndPaymentPage />,
                  },
                  {
                    path: CreatePermanentJobPostingSteps.WorkDetails,
                    element: <CreatePermanentJobPosting.WorkDetailsPage />,
                  },
                ],
              },
              {
                path: '/permanent-jobs/postings/:id/onsite-interviews/create',
                element: <CreateOnsiteInterviewPage />,
                children: [
                  {
                    path: '',
                    element: <Navigate to={CreateOnsiteInterviewSteps.WorkDetails} replace />,
                  },
                  {
                    path: CreateOnsiteInterviewSteps.Reviews,
                    element: <CreateOnsiteInterviewSubPages.ReviewAndConfirmPage />,
                  },
                  {
                    path: CreateOnsiteInterviewSteps.WorkDetails,
                    element: <CreateOnsiteInterviewSubPages.WorkDetailsPage />,
                  },
                ],
              },
              {
                path: '/permanent-jobs/postings/:postingId/onsite-interviews/:onsiteInterviewId/edit',
                element: <EditOnsiteInterviewPage />,
              },
              {
                path: '/permanent-jobs/job-overview/:postingId/:type?',
                element: <JobOverview />,
              },
            ]
          : []),
        ...(isEnablePermanentJobM2M
          ? [
              {
                title: 'Candidate Overview',
                path: '/permanent-jobs/:jobPostingId/candidates/:professionalId/overview',
                element: <CandidateOverview />,
              },
              {
                title: 'Candidate Overview Redirect',
                path: '/permanent-jobs/candidate-overview',
                element: <CandidateOverviewRedirect />,
              },
            ]
          : []),
      ],
    },
  ]);
};
